import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@apollo/client';
import { Platform, ScrollView, View } from 'react-native';
import moment from 'moment';

import { useUserContext } from '../../../common/utils/context';
import { GET_WAREHOUSES } from '../../../common/graphql/warehouse';
import Text from '../../components/text/Text';
import { TextInput } from '../../components/input';
import { Card } from '../../design/components';
import { UserSelector, WarehouseSelector } from '../../components/selector';
import PickerButton from '../../components/picker/PickerButton/PickerButton';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import useReportDockAction from '../../hooks/useReportDockAction';
import { CreatorContainer } from '../../containers';

let DateTimePickerModal;
if (Platform.OS === 'web') {
	DateTimePickerModal = require('../../components/DateTimePickerModal.web').default;
}
else {
	DateTimePickerModal = require('react-native-modal-datetime-picker').default;
}

const StocktakeCreator = ({
	values,
	changeFormValue,
	onSubmit,
	submitting,
	onDockChange,
}) => {
	const styles = useThemedStyles(createStyles);
	const { user } = useUserContext();
	const [showDatePicker, setShowDatePicker] = useState(false);

	const { data: warehousesData } = useQuery(GET_WAREHOUSES, {
		variables: {
			page: 0,
			perPage: 1,
			sortBy: 'name',
			sortDirection: 1,
			filter: {},
		},
		fetchPolicy: 'cache-first',
	});

	const defaultWarehouse = useMemo(() => {
		return warehousesData?.getWarehouses?.items?.[0] || null;
	}, [warehousesData]);
	const hasAppliedDefaultWarehouse = useRef(false);

	useEffect(() => {
		if (user && !values.user) {
			changeFormValue({ user });
		}
	}, [changeFormValue, user, values.user]);

	useEffect(() => {
		if (!hasAppliedDefaultWarehouse.current && defaultWarehouse) {
			changeFormValue({ warehouse: defaultWarehouse });
			hasAppliedDefaultWarehouse.current = true;
		}
	}, [changeFormValue, defaultWarehouse]);

	const formattedDate = useMemo(() => {
		return values.date ? moment(values.date).format('DD MMM YYYY') : 'Select date';
	}, [values.date]);

	const selectedDateValue = useMemo(() => {
		return values.date ? new Date(values.date) : new Date();
	}, [values.date]);

	const canSubmit = Boolean(values.warehouse && values.user && values.date);

	const handleDescriptionChange = useCallback((text) => {
		changeFormValue({ description: text });
	}, [changeFormValue]);

	const handleDateConfirm = useCallback((selectedDate) => {
		const resolvedDate = selectedDate ? moment(selectedDate).startOf('day').toDate() : new Date();
		changeFormValue({ date: resolvedDate.toISOString() });
		setShowDatePicker(false);
	}, [changeFormValue]);

	useReportDockAction(onDockChange, {
		label: 'Create stocktake',
		onPress: onSubmit,
		loading: submitting,
		disabled: !canSubmit || submitting,
	});

	return (
		<View style={styles.container}>
			<ScrollView
				style={styles.scroll}
				contentContainerStyle={styles.contentContainer}
				keyboardShouldPersistTaps="handled"
			>
				<Card>
					<View style={styles.field}>
						<Text style={styles.label}>Date</Text>
						<PickerButton onPress={() => setShowDatePicker(true)}>
							{formattedDate}
						</PickerButton>
					</View>

					<View style={[styles.field, styles.fieldSpacing]}>
						<Text style={styles.label}>Description</Text>
						<TextInput
							style={styles.textArea}
							value={values.description || ''}
							multiline={true}
							numberOfLines={4}
							onChange={handleDescriptionChange}
							placeholder="Optional description"
						/>
					</View>
				</Card>

				<Card>
					<View style={styles.field}>
						<Text style={styles.label}>Warehouse</Text>
						<WarehouseSelector
							value={values.warehouse}
							initialValue={values.warehouse}
							forceControlled={true}
							onChange={warehouse => changeFormValue({ warehouse })}
						/>
					</View>

					<View style={[styles.field, styles.fieldSpacing]}>
						<Text style={styles.label}>Person performing stocktake</Text>
						<UserSelector
							value={values.user}
							initialValue={values.user}
							forceControlled={true}
							onChange={newUser => changeFormValue({ user: newUser })}
						/>
					</View>
				</Card>
			</ScrollView>
			<DateTimePickerModal
				isVisible={showDatePicker}
				mode="date"
				date={selectedDateValue}
				onConfirm={handleDateConfirm}
				onCancel={() => setShowDatePicker(false)}
			/>
		</View>
	);
};

const createStyles = ({ ui: { colors, type, space } }) => ({
	container: {
		flex: 1,
	},
	scroll: {
		flex: 1,
	},
	contentContainer: {
		paddingHorizontal: space.gutter,
		paddingBottom: space.lg,
		gap: space.md,
		flexGrow: 1,
	},
	label: {
		...type.fieldLabel,
		marginBottom: 6,
		color: colors.text,
	},
	textArea: {
		minHeight: 100,
		textAlignVertical: 'top',
		paddingTop: 10,
		paddingBottom: 10,
	},
	field: {
		width: '100%',
	},
	fieldSpacing: {
		marginTop: space.md,
	},
});

export default CreatorContainer(StocktakeCreator);
